import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2, Save } from 'lucide-react';
import { PageHeader } from '../../components/layout';
import {
    Card, CardBody, Button, Input, TextArea, Select,
    DatePicker, toast
} from '../../components/common';
import { contractApi, rfqApi, vendorApi } from '../../services/api';
import './ContractForm.css';

export default function ContractForm() {
    const navigate = useNavigate();
    const { id } = useParams();
    const [searchParams] = useSearchParams();
    const rfqId = searchParams.get('rfqId');
    const queryClient = useQueryClient();
    const isEditing = !!id;

    const [form, setForm] = useState({
        title: '',
        description: '',
        vendorId: '',
        rfqId: rfqId || '',
        contractType: 'fixed_price',
        totalValue: '',
        startDate: '',
        endDate: '',
        paymentTerms: '',
        termsAndConditions: '',
        milestones: []
    });
    const [errors, setErrors] = useState({});

    // Fetch existing contract if editing
    const { data: contractData } = useQuery({
        queryKey: ['contract', id],
        queryFn: () => contractApi.getById(id),
        enabled: isEditing,
        select: (res) => res.data?.data
    });

    // Fetch RFQ if creating from RFQ
    const { data: rfqData } = useQuery({
        queryKey: ['rfq', rfqId],
        queryFn: () => rfqApi.get(rfqId),
        enabled: !!rfqId && !isEditing,
        select: (res) => res.data?.data
    });

    // Fetch vendors
    const { data: vendors } = useQuery({
        queryKey: ['vendors', 'approved'],
        queryFn: () => vendorApi.list({ status: 'approved', limit: 100 }),
        select: (res) => res.data?.data || []
    });

    useEffect(() => {
        if (contractData) {
            setForm({
                title: contractData.title || '',
                description: contractData.description || '',
                vendorId: contractData.vendorId?.toString() || '',
                rfqId: contractData.rfqId?.toString() || '',
                contractType: contractData.contractType || 'fixed_price',
                totalValue: contractData.totalValue?.toString() || '',
                startDate: contractData.startDate?.split('T')[0] || '',
                endDate: contractData.endDate?.split('T')[0] || '',
                paymentTerms: contractData.paymentTerms || '',
                termsAndConditions: contractData.termsAndConditions || '',
                milestones: contractData.milestones || []
            });
        } else if (rfqData) {
            // Pre-fill from awarded RFQ
            const awardedQuotation = rfqData.quotations?.find(q => q.isAwarded);
            setForm(prev => ({
                ...prev,
                title: `Contract for ${rfqData.title}`,
                vendorId: awardedQuotation?.vendorId?.toString() || '',
                rfqId: rfqId,
                totalValue: awardedQuotation?.total?.toString() || ''
            }));
        }
    }, [contractData, rfqData, rfqId]);

    const saveMutation = useMutation({
        mutationFn: (data) => isEditing
            ? contractApi.update?.(id, data) || Promise.resolve()
            : (rfqId ? contractApi.createFromRFQ(rfqId, data) : contractApi.create(data)),
        onSuccess: (res) => {
            toast.success(`Contract ${isEditing ? 'updated' : 'created'} successfully`);
            queryClient.invalidateQueries(['contracts']);
            navigate(`/contracts/${res.data?.data?.id || id}`);
        },
        onError: (err) => {
            toast.error(err.response?.data?.error?.message || 'Failed to save');
        }
    });

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm(prev => ({ ...prev, [name]: value }));
        setErrors(prev => ({ ...prev, [name]: '' }));
    };

    const handleAddMilestone = () => {
        setForm(prev => ({
            ...prev,
            milestones: [...prev.milestones, { title: '', dueDate: '', amount: '' }]
        }));
    };

    const handleUpdateMilestone = (index, field, value) => {
        setForm(prev => ({
            ...prev,
            milestones: prev.milestones.map((m, i) =>
                i === index ? { ...m, [field]: value } : m
            )
        }));
    };

    const handleRemoveMilestone = (index) => {
        setForm(prev => ({
            ...prev,
            milestones: prev.milestones.filter((_, i) => i !== index)
        }));
    };

    const validate = () => {
        const newErrors = {};
        if (!form.title) newErrors.title = 'Title is required';
        if (!form.vendorId) newErrors.vendorId = 'Vendor is required';
        if (!form.totalValue) newErrors.totalValue = 'Total value is required';
        if (!form.startDate) newErrors.startDate = 'Start date is required';
        if (!form.endDate) newErrors.endDate = 'End date is required';
        setErrors(newErrors);

        if (Object.keys(newErrors).length > 0) {
            const errorMessages = Object.values(newErrors).join(', ');
            toast.error(`Please fix the following errors: ${errorMessages}`);
            return false;
        }
        return true;
    };

    const handleSave = () => {
        if (!validate()) return;
        saveMutation.mutate({
            ...form,
            vendorId: form.vendorId,
            rfqId: form.rfqId || null,
            totalValue: parseFloat(form.totalValue),
            milestones: form.milestones.map(m => ({
                title: m.title,
                dueDate: m.dueDate,
                amount: parseFloat(m.amount) || 0
            }))
        });
    };

    const vendorOptions = vendors?.map(v => ({ value: v.id.toString(), label: v.name })) || [];
    const contractTypeOptions = [
        { value: 'fixed_price', label: 'Fixed Price' },
        { value: 'time_materials', label: 'Time & Materials' },
        { value: 'unit_rate', label: 'Unit Rate' },
        { value: 'blanket', label: 'Blanket/Framework' }
    ];

    return (
        <div className="contract-form-page">
            <PageHeader
                title={isEditing ? 'Edit Contract' : 'Create Contract'}
                breadcrumbs={[
                    { label: 'Contracts', path: '/contracts' },
                    { label: isEditing ? 'Edit' : 'Create' }
                ]}
            />

            <div className="contract-form-content">
                <Card>
                    <CardBody>
                        <h3>Contract Details</h3>
                        <div className="form-grid">
                            <div className="form-full">
                                <Input
                                    name="title"
                                    label="Contract Title"
                                    placeholder="Enter contract title"
                                    value={form.title}
                                    onChange={handleChange}
                                    error={errors.title}
                                    required
                                />
                            </div>
                            <Select
                                name="vendorId"
                                label="Vendor"
                                value={form.vendorId}
                                onChange={handleChange}
                                options={vendorOptions}
                                error={errors.vendorId}
                                required
                            />
                            <Select
                                name="contractType"
                                label="Contract Type"
                                value={form.contractType}
                                onChange={handleChange}
                                options={contractTypeOptions}
                            />
                            <Input
                                name="totalValue"
                                type="number"
                                label="Total Value"
                                placeholder="0"
                                value={form.totalValue}
                                onChange={handleChange}
                                error={errors.totalValue}
                                required
                            />
                            <div></div>
                            <DatePicker
                                label="Start Date"
                                value={form.startDate}
                                onChange={(date) => setForm(prev => ({ ...prev, startDate: date }))}
                                error={errors.startDate}
                                required
                            />
                            <DatePicker
                                label="End Date"
                                value={form.endDate}
                                onChange={(date) => setForm(prev => ({ ...prev, endDate: date }))}
                                minDate={form.startDate}
                                error={errors.endDate}
                                required
                            />
                            <div className="form-full">
                                <TextArea
                                    name="description"
                                    label="Description"
                                    placeholder="Contract scope and deliverables"
                                    value={form.description}
                                    onChange={handleChange}
                                    rows={3}
                                />
                            </div>
                            <div className="form-full">
                                <TextArea
                                    name="paymentTerms"
                                    label="Payment Terms"
                                    placeholder="Payment schedule and terms"
                                    value={form.paymentTerms}
                                    onChange={handleChange}
                                    rows={2}
                                />
                            </div>
                            <div className="form-full">
                                <TextArea
                                    name="termsAndConditions"
                                    label="Terms & Conditions"
                                    placeholder="Contract terms and conditions"
                                    value={form.termsAndConditions}
                                    onChange={handleChange}
                                    rows={3}
                                />
                            </div>
                        </div>
                    </CardBody>
                </Card>

                <Card>
                    <CardBody>
                        <div className="milestones-header">
                            <h3>Milestones</h3>
                            <Button size="sm" variant="secondary" icon={Plus} onClick={handleAddMilestone}>
                                Add Milestone
                            </Button>
                        </div>

                        {form.milestones.length === 0 ? (
                            <div className="milestones-empty">
                                <p>No milestones defined. Add milestones for payment tracking.</p>
                            </div>
                        ) : (
                            <div className="milestones-list">
                                {form.milestones.map((milestone, index) => (
                                    <div key={index} className="milestone-item">
                                        <Input
                                            placeholder="Milestone title"
                                            value={milestone.title}
                                            onChange={(e) => handleUpdateMilestone(index, 'title', e.target.value)}
                                        />
                                        <input
                                            type="date"
                                            value={milestone.dueDate}
                                            onChange={(e) => handleUpdateMilestone(index, 'dueDate', e.target.value)}
                                            className="date-input"
                                        />
                                        <input
                                            type="number"
                                            placeholder="Amount"
                                            value={milestone.amount}
                                            onChange={(e) => handleUpdateMilestone(index, 'amount', e.target.value)}
                                            className="amount-input"
                                        />
                                        <button className="remove-btn" onClick={() => handleRemoveMilestone(index)}>
                                            <Trash2 size={14} />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </CardBody>
                </Card>

                <div className="contract-form-actions">
                    <Button variant="secondary" onClick={() => navigate('/contracts')}>
                        Cancel
                    </Button>
                    <Button icon={Save} onClick={handleSave} loading={saveMutation.isPending}>
                        {isEditing ? 'Update Contract' : 'Save Contract'}
                    </Button>
                </div>
            </div>
        </div>
    );
}
